import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Slider, Modal, notification } from 'antd';
import { ScissorOutlined, CameraOutlined, LoadingOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';

// ── Photo crop + upload helpers ───────────────────────────────────────────────
const createImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.addEventListener('load', () => resolve(img));
  img.addEventListener('error', reject);
  img.setAttribute('crossOrigin', 'anonymous');
  img.src = url;
});

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image  = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx    = canvas.getContext('2d');
  const maxDim = 900;
  const scale  = Math.min(maxDim / pixelCrop.width, maxDim / pixelCrop.height, 1);
  canvas.width  = Math.round(pixelCrop.width  * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
}

async function doPhotoUpload(blob, type) {
  const fd = new FormData();
  fd.append('photo', blob, 'photo.jpg');
  const res  = await fetch(`${baseUrl}/business-directory/upload-photo?type=${type}`, { method: 'POST', body: fd });
  const data = await res.json();
  if (!data.status) throw new Error(data.message || 'Upload failed');
  return data.url;
}

export function CropModal({ open, imageSrc, aspect, onDone, onCancel }) {
  const [crop, setCrop]   = useState({ x: 0, y: 0 });
  const [zoom, setZoom]   = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy]   = useState(false);
  const onComplete = useCallback((_, p) => setPixels(p), []);
  const apply = async () => {
    if (!pixels) return;
    setBusy(true);
    try { onDone(await getCroppedBlob(imageSrc, pixels)); }
    catch { notification.error({ message: 'Crop failed', placement: 'topRight' }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onCancel={onCancel} width={340} centered destroyOnClose
      title={<span style={{ color: M }}><ScissorOutlined style={{ marginRight: 8 }} />फोटो काटें</span>}
      footer={[
        <Button key="c" onClick={onCancel}>रद्द करें</Button>,
        <Button key="a" loading={busy} onClick={apply}
          style={{ background: M, borderColor: M, color: G, fontWeight: 700 }}>सेट करें</Button>,
      ]}>
      <div style={{ position: 'relative', height: 240, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
        {imageSrc && <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspect}
          onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />}
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>ज़ूम</div>
        <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom}
          trackStyle={{ background: M }} handleStyle={{ borderColor: M }} />
      </div>
    </Modal>
  );
}

const isValidImgUrl = v => v && (v.startsWith('http') || v.startsWith('/') || v.startsWith('data:'));

export function ProfilePhotoUpload({ value, onChange }) {
  const [preview, setPreview]     = useState(isValidImgUrl(value) ? value : null);
  const [uploading, setUploading] = useState(false);
  const [rawSrc, setRawSrc]       = useState(null);
  const [cropOpen, setCropOpen]   = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { setPreview(isValidImgUrl(value) ? value : null); }, [value]);

  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'JPG या PNG चुनें।', placement: 'topRight' }); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notification.warning({ message: 'Photo 5MB से छोटी होनी चाहिए।', placement: 'topRight' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = '';
  };
  const onCropDone = async (blob) => {
    setCropOpen(false); setUploading(true);
    try { const url = await doPhotoUpload(blob, 'profile'); setPreview(url); onChange(url); }
    catch (err) { notification.error({ message: 'Upload failed: ' + err.message, placement: 'topRight' }); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])} />
      <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ width: 80, height: 80, borderRadius: 10, border: `2.5px solid ${M}`,
            overflow: 'hidden', cursor: 'pointer', background: '#fdf5f5' }}>
          {preview
            ? <img src={preview} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }} />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                {uploading ? <LoadingOutlined style={{ fontSize: 22, color: M }} /> : <UserOutlined style={{ fontSize: 28, color: '#ccc' }} />}
              </div>
          }
        </div>
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ position: 'absolute', bottom: -4, right: -4, width: 24, height: 24,
            borderRadius: 7, background: M, border: '2px solid #fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <CameraOutlined style={{ color: G, fontSize: 11 }} />
        </div>
        {preview && !uploading && (
          <button type="button" onClick={e => { e.stopPropagation(); setPreview(null); onChange(null); }}
            style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18,
              borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
              color: '#fff', fontSize: 9, cursor: 'pointer', lineHeight: '14px', textAlign: 'center', padding: 0 }}>✕</button>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.7 }}>
        प्रोफाइल फोटो<br />
        <span style={{ color: '#9ca3af' }}>JPG / PNG · max 5MB</span><br />
        <span style={{ color: M, cursor: 'pointer', fontWeight: 600 }}
          onClick={() => !uploading && inputRef.current?.click()}>
          {preview ? 'बदलें' : 'अपलोड करें'}
        </span>
      </div>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={1} onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </div>
  );
}

export function BusinessPhotoUpload({ value, onChange }) {
  const [slots, setSlots]         = useState(() => { const u = value ? value.split(',').filter(Boolean) : []; return [u[0]||null, u[1]||null]; });
  const [uploading, setUploading] = useState([false, false]);
  const [rawSrc, setRawSrc]       = useState(null);
  const [cropOpen, setCropOpen]   = useState(false);
  const [pendingIdx, setPendingIdx] = useState(null);
  const refs = [useRef(null), useRef(null)];

  useEffect(() => {
    const u = value ? value.split(',').filter(Boolean) : [];
    setSlots([u[0]||null, u[1]||null]);
  }, [value]);

  // eslint-disable-next-line no-unused-vars
  const notify = (s) => onChange(s.filter(Boolean).join(',') || null);

  const handleFile = (file, idx) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'JPG या PNG चुनें।', placement: 'topRight' }); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notification.warning({ message: 'Photo 5MB से छोटी होनी चाहिए।', placement: 'topRight' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setPendingIdx(idx); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (refs[idx].current) refs[idx].current.value = '';
  };
  const onCropDone = async (blob) => {
    setCropOpen(false);
    const idx = pendingIdx;
    setUploading(u => { const n=[...u]; n[idx]=true; return n; });
    try {
      const url = await doPhotoUpload(blob, 'business');
      const newSlots = [...slots];
      newSlots[idx] = url;
      setSlots(newSlots);
      onChange(newSlots.filter(Boolean).join(',') || null);
    } catch (err) { notification.error({ message: 'Upload failed: ' + err.message, placement: 'topRight' }); }
    finally { setUploading(u => { const n=[...u]; n[idx]=false; return n; }); }
  };
  const remove = (idx) => {
    const newSlots = [...slots];
    newSlots[idx] = null;
    setSlots(newSlots);
    onChange(newSlots.filter(Boolean).join(',') || null);
    if (refs[idx].current) refs[idx].current.value = '';
  };

  return (
    <>
      {[0,1].map(idx => (
        <input key={idx} ref={refs[idx]} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0], idx)} />
      ))}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {[0,1].map(idx => (
          slots[idx] ? (
            <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
              <img src={slots[idx]} alt={`biz-${idx+1}`} onClick={() => refs[idx].current?.click()}
                style={{ width: 100, height: 75, borderRadius: 8, objectFit: 'cover',
                  border: `2px solid ${M}`, cursor: 'pointer', display: 'block' }} />
              <button type="button" onClick={() => remove(idx)}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                  borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
                  color: '#fff', fontSize: 9, cursor: 'pointer', lineHeight: '14px', textAlign: 'center', padding: 0 }}>✕</button>
            </div>
          ) : uploading[idx] ? (
            <div key={idx} style={{ width: 100, height: 75, borderRadius: 8,
              border: `2px dashed ${M}`, background: '#fdf5f5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LoadingOutlined style={{ fontSize: 18, color: M }} />
            </div>
          ) : (
            <button key={idx} type="button" onClick={() => refs[idx].current?.click()}
              style={{ width: 100, height: 75, borderRadius: 8, border: `2px dashed ${M}`,
                background: '#fdf5f5', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
              <PlusOutlined style={{ fontSize: 16, color: M }} />
              <span style={{ fontSize: 10, color: M }}>फोटो {idx+1}</span>
            </button>
          )
        ))}
        <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.8, alignSelf: 'center' }}>
          <CameraOutlined style={{ fontSize: 14, color: M, display: 'block', marginBottom: 2 }} />
          दुकान / फर्म फोटो<br />JPG / PNG · max 5MB
        </div>
      </div>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={4/3} onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </>
  );
}
